import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { EDITABLE_DOCUMENT_PROPS } from "../..";
import { INPUT_FIELD_NAMES, INPUT_FIELD_NAMES_ARRAY } from "nlib/common/ContactForm/constants";
import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getCurrentZohoOrganizationId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData
} from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AwaitingButton from "nlib/common/AwaitingButton";
import Button from "nlib/ui/Button";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentTypes from "const/DocumentTypes";
import DocumentsActions from "actions/DocumentsActions";
import Files from "utils/Files";
import FormContext from "contexts/FormContext";
import IntegrationServices from "const/IntegrationServices";
import KeyNames from "const/KeyNames";
import MainApiActions from "actions/MainApiActions";
import Objects from "utils/Objects";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import Texts from "utils/Texts";
import ValidationSchemes from "const/ValidationSchemes";
import moment from "moment";
import useShowCommonModal from "hooks/useShowCommonModal";

const ADDRESS_FIELDS_TO_COMPARE = INPUT_FIELD_NAMES_ARRAY.filter((field) => field !== INPUT_FIELD_NAMES.TYPE);

const DOCUMENT_FROZEN_STATUSES = [DataStatuses.TO_REPORT, DataStatuses.EXCLUDED, DataStatuses.EXPORTED];

const DEFAULTS = { issueDate: "", dueDate: "", originalDocumentId: "" };

const Header = (props) => {
  const {
    disabled,
    openedInWindow,
    documentData = {},
    extractedDocumentsData = [],
    resetDuplicatedDocumentsIds,
    openDocument,
    onClose
  } = props;

  const {
    id: documentId,
    address,
    vendorId,
    createdAt,
    createdBy,
    sourceType,
    attachment = {},
    paidTransactions = [],
    originalDocumentId = "",
    duplicatedDocumentsIds
  } = documentData;

  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const { uiTexts, messages } = useSelector(getTextsData);

  const businessUser = useSelector(checkIsBusinessUser);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const zohoBusiness = !!useSelector(getCurrentZohoOrganizationId);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const { countryCode } = useSelector(getActiveOrganization);

  const {
    extraData: { integrationService, integrationServiceConnected } = {}
  } = useSelector(getSelectedBusinessData);

  const { values, onSubmit } = useContext(FormContext);

  const { label: serviceName } = IntegrationServices.getByValue(integrationService) || {};

  const closedBookDate = useMemo(() => {
    return selectedBusinessBookCloseDate
      && moment.utc(values.issueDate).isSameOrBefore(moment.utc(selectedBusinessBookCloseDate));
  }, [values.issueDate, selectedBusinessBookCloseDate]);

  const usCountry = countryCode === Countries.US;

  const currentDocumentIndex = extractedDocumentsData.findIndex(({ id }) => id === documentData.id);

  const duplicatedDocument = !!(duplicatedDocumentsIds && duplicatedDocumentsIds.length);

  const businessName = (vendorId && address && address.name) || "";

  const documentFrozen = documentData && DOCUMENT_FROZEN_STATUSES.includes(documentData.status);

  const disableToReportButton = useMemo(() => {
    const normalizedValues = { ...values, amountVat: values.amountVat || 0 };

    if (businessUser || disabled || !integrationServiceConnected || documentFrozen) return true;

    const { paid, issueDate, paymentDate } = normalizedValues;

    if (
      (xeroBusiness || zohoBusiness) && paid && !paidTransactions.length
        && (!paymentDate || (issueDate && moment.utc(issueDate).isAfter(moment.utc(paymentDate))))) {
      return true;
    }
    if (paidTransactions.length && normalizedValues.type === DocumentTypes.SALES_RECEIPT) {
      return true;
    }

    let lineItemsValidationResult = false;

    try {
      ValidationSchemes.ACCOUNTANT_DOCUMENT_LINE_ITEM_VALIDATION_SCHEMA.validateSync(normalizedValues, {
        context: {
          ...normalizedValues,
          xeroBusiness,
          taxRequired: quickBooksBusiness && !usCountry
            && normalizedValues.lineItems.some(({ taxRate = {} }) => taxRate && taxRate.id)
        }
      });
      lineItemsValidationResult = true;
    } catch (exception) {}
    if (!lineItemsValidationResult) return true;

    const { DOCUMENT_VALIDATION_SCHEMA, RECEIPT_VALIDATION_SCHEMA } = ValidationSchemes;

    const validationSchema = normalizedValues.type === DocumentTypes.RECEIPT
      ? RECEIPT_VALIDATION_SCHEMA
      : DOCUMENT_VALIDATION_SCHEMA;

    let documentValidationResult = false;

    try {
      validationSchema.validateSync(normalizedValues, {
        context: {
          paid: normalizedValues.paid,
          rpaMode,
          exportAs: normalizedValues.exportAs
        }
      });
      documentValidationResult = true;
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.log("Document validation errors", exception?.errors);
    }
    if (!documentValidationResult) return true;

    return false;
  }, [
    businessUser,
    disabled,
    integrationServiceConnected,
    documentFrozen,
    values,
    xeroBusiness,
    zohoBusiness,
    quickBooksBusiness,
    paidTransactions.length,
    usCountry,
    rpaMode
  ]);

  const { fullName, email } = createdBy;

  const createdByText = (fullName ? fullName + (fullName === email ? "" : ` <${email}>`) : email)
    || uiTexts.unknown.toLowerCase();

  const createdAtText = moment(createdAt).format(DateTimeFormats.DATETIME_TEXT);

  const hasChanges = useMemo(() => {
    const { address: initialAddress, ...restDocument }
      = Objects.getProps(documentData, EDITABLE_DOCUMENT_PROPS, DEFAULTS);

    const { address: editedAddress, abnActive, abnStatusFetching, ...restValues } = values;

    if (!Objects.checkDeepEqualityByProps(restDocument, restValues)) {
      return true;
    }

    return !values.vendorId && !Objects.checkDeepEqualityByProps(
      Objects.getProps(initialAddress, ADDRESS_FIELDS_TO_COMPARE),
      Objects.getProps(editedAddress, ADDRESS_FIELDS_TO_COMPARE)
    );
  }, [documentData, values]);

  const closeDocument = useCallback(async(nextDocumentId = null) => {
    if (hasChanges) {
      const modalResult = await showCommonModal({
        text: messages.unsavedChangesWarning,
        headerText: uiTexts.warning,
        confirm: true,
        okButtonText: uiTexts.yes,
        cancelButtonText: uiTexts.no
      });

      if (modalResult) {
        await onSubmit();
      }
      onClose(modalResult);
    } else if (resetDuplicatedDocumentsIds) {
      if (!nextDocumentId) {
        await dispatch(
          DocumentsActions.editDocument(documentId, { duplicatedDocumentsIds: [] }, false, false, false, true)
        );
      }
      onClose(!nextDocumentId);
    } else {
      onClose();
    }
    if (nextDocumentId) openDocument(nextDocumentId);
  }, [
    hasChanges,
    messages,
    uiTexts,
    showCommonModal,
    dispatch,
    documentId,
    resetDuplicatedDocumentsIds,
    onSubmit,
    openDocument,
    onClose
  ]);

  const switchDocument = useCallback(async(index) => {
    const nextDocument = extractedDocumentsData[currentDocumentIndex + index];

    if (!nextDocument) return;
    await closeDocument(nextDocument.id);
  }, [currentDocumentIndex, extractedDocumentsData, closeDocument]);

  const handleCloseClick = useCallback(() => {
    closeDocument();
  }, [closeDocument]);

  const handleFormSubmit = useCallback(async(params) => {
    const nextDocument = extractedDocumentsData[currentDocumentIndex + 1];

    const result = await onSubmit(params);

    if (result && nextDocument && openDocument) openDocument(nextDocument.id);
  }, [currentDocumentIndex, extractedDocumentsData, onSubmit, openDocument]);

  const handleToReportClick = useCallback(() => {
    handleFormSubmit({ toReport: true });
  }, [handleFormSubmit]);

  const handleExcludeClick = useCallback(() => {
    handleFormSubmit({ toExclude: true });
  }, [handleFormSubmit]);

  const handlePrevDocumentButtonClick = useCallback(() => {
    switchDocument(-1);
  }, [switchDocument]);

  const handleNextDocumentButtonClick = useCallback(() => {
    switchDocument(1);
  }, [switchDocument]);

  const handleDownloadClick = useCallback(async() => {
    const link = await dispatch(MainApiActions.fetchAttachmentUrl(attachment, true));

    if (!link) return;
    Files.downloadContent(link);
  }, [attachment, dispatch]);

  const handleKeyDown = useCallback((event) => {
    if (disabled) return;
    if (event.key === KeyNames.ESCAPE) {
      handleCloseClick(event);

      return;
    }
    if (event.key.toLowerCase() === KeyNames.KEY_S && hasChanges && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (event.shiftKey) {
        if (!disableToReportButton) {
          handleFormSubmit({ toReport: true });
        }
      } else {
        handleFormSubmit();
      }
    }
  }, [disabled, disableToReportButton, handleCloseClick, handleFormSubmit, hasChanges]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className={Css.header} id="edit-document-header">
      <div className={Css.info}>
        <div className={Css.title}>
          {duplicatedDocument
            ? <Icons.Copy className={Css.warning} />
            : <Icons.FileText />}
          <b className={Css.documentId}>
            {originalDocumentId ? `#${originalDocumentId}` : ""}
          </b>
          <span className={businessName ? Css.muted : ""}>
            {businessName
              ? `${originalDocumentId ? " | " : ""}${businessName}`
              : `${originalDocumentId ? " — " : ""}${uiTexts.editDocument}`}
          </span>
        </div>
        <div className={Css.subtitle} title={`${uiTexts.uploadedBy}: ${createdByText}, ${createdAtText}`}>
          {attachment.key
            ? (
              <>
                <span>{`${sourceType === "email" ? uiTexts.emailedBy : uiTexts.uploadedBy}: `}</span>
                <b>{createdByText}</b>
              </>
            )
            : Texts.replaceVars(uiTexts.importedFrom, { serviceName })}
          <span>{`, ${createdAtText}`}</span>
        </div>
      </div>
      {!!attachment.key && (
        <AwaitingButton
          large outline
          disabled={disabled}
          icon={Icons.Download}
          className={Css.download}
          title={uiTexts.download}
          onClick={handleDownloadClick}>
          {uiTexts.download}
        </AwaitingButton>
      )}
      {!openedInWindow && !documentFrozen && !businessUser && documentData.status !== DataStatuses.EXCLUDED && (
        <Button
          large outline
          disabled={disabled || documentFrozen}
          onClick={handleExcludeClick}>
          {uiTexts.exclude}
        </Button>
      )}
      {(extractedDocumentsData.length > 1) && !openedInWindow && (
        <div className={Css.switchButtons}>
          <Button
            large outline
            disabled={currentDocumentIndex === 0 || disabled}
            onClick={handlePrevDocumentButtonClick}>
            <Icons.ArrowLeft />
          </Button>
          <div className={Css.counter}>
            {`${currentDocumentIndex + 1}/${extractedDocumentsData.length}`}
          </div>
          <Button
            large outline
            disabled={currentDocumentIndex === extractedDocumentsData.length - 1 || disabled}
            onClick={handleNextDocumentButtonClick}>
            <span>{uiTexts.nextDocument}</span>
            <Icons.ArrowRight />
          </Button>
        </div>
      )}
      <div className={Css.actions}>
        <Button
          large outline
          disabled={disabled}
          className={Css.closeButton}
          icon={Icons.X}
          onClick={handleCloseClick}>{uiTexts.close}</Button>
        {!documentFrozen && (
          <Button
            large primary
            disabled={disabled || !hasChanges || values.abnStatusFetching}
            onClick={handleFormSubmit}>
            {openedInWindow ? uiTexts.save : uiTexts.saveAndClose}
          </Button>
        )}
        {!businessUser && (documentData.status === DataStatuses.TO_REVIEW) && !documentFrozen && (
          <Button
            large
            outline={closedBookDate}
            success={!closedBookDate}
            disabled={disableToReportButton}
            className={closedBookDate ? Css.warning : undefined}
            icon={!documentFrozen && closedBookDate && Icons.Warning}
            onClick={handleToReportClick}>
            {openedInWindow ? uiTexts.approve : uiTexts.approveAndClose}
          </Button>
        )}
      </div>
    </div>
  );
};

export default React.memo(Header);
